/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./api/QueryModifierAPI',
	'./DataQueryUtils'
], (_, APIFactory, QueryModifierAPI) => {
	class SharedSlotVisQueryModifier {

		constructor({ content, features }) {
			this.content = content;
			features.DataQueryExecution.registerQueryModifier(this.getAPI());
		}

		getAPI() {
			if (!this.api) {
				this.api = APIFactory.createAPI(this, [QueryModifierAPI]);
			}
			return this.api;
		}

		getType() {
			return 'main';
		}

		modifyQuerySpecList(querySpecList) {
			const visualization = this.content.getFeature('Visualization');
			const mappedSlots = visualization.getSlots().getMappedSlotList();

			if (this._hasSharedSlot(mappedSlots)) {
				this._addExtraDataItems(querySpecList, mappedSlots);
			}
			return querySpecList;
		}

		_hasSharedSlot(mappedSlots) {
			const sharedSlots = mappedSlots.filter(slot => slot.getDefinition().getDatasetIdList().length > 1);
			return sharedSlots.length > 0;
		}

		_getDataItemMap(querySpecList) {
			const result = {};
			querySpecList.forEach(querySpec => {
				querySpec.spec.dataItems.forEach(dataItem => {
					if (!result[dataItem.id]) {
						result[dataItem.id] = dataItem;
					}
				});
			});
			return result;
		}

		_getNestIdList(querySpec) {
			const result = [];
			querySpec.dataItems.forEach(dataItem => {
				if (dataItem.nest) {
					result.push(...dataItem.nest);
				}
			});
			return result;
		}

		/**
		 * Get the extra data items from other datasets
		 * We only need to copy data items with type fact
		 *
		 * @param {*} mappedSlots
		 * @param {*} datasetId
		 * @returns
		 * @memberof SharedSlotVisQueryModifier
		 */
		_getExtraDataItemIdList(querySpec, mappedSlots) {
			const extraDataItemIdList = [];
			const projections = querySpec.spec.projections;
			const nestIdList = this._getNestIdList(querySpec.spec);
			const excludedIdList = [...projections, ...nestIdList];
			mappedSlots.forEach(slot => {
				slot.getDataItemList().forEach(dataItem => {
					const id = dataItem.getId();
					if(dataItem.getType() === 'fact' && !excludedIdList.includes(id)) {
						extraDataItemIdList.push(id);
					}
				});
			});
			return extraDataItemIdList;
		}

		_addExtraDataItems(querySpecList, mappedSlots) {
			const dataItemMap = this._getDataItemMap(querySpecList);
			querySpecList.forEach(querySpec => {
				const extraDataItems = [];
				const extraDataItemIdList = this._getExtraDataItemIdList(querySpec, mappedSlots);

				extraDataItemIdList.forEach(id => {
					const dataItem = dataItemMap[id];
					if (this._isValidDataItem(dataItem)) {
						/*
							When sorting by a fact in one of the Bar/line queries, we need to introduce that
							fact item and sort spec into the other query as a context item.
							The query layer will use that item to produce a sorted result.
						*/
						extraDataItems.push(dataItem);
					}
				});
				if (extraDataItems.length) {
					querySpec.spec.dataItems.push(...extraDataItems);
				}

				this._modifyMultiEdgeSort(querySpec);
			});
		}

		/**
		 *
		 * Return true is the data item has sorting spec in selection
		 * @memberof SharedSlotVisQueryModifier
		 */
		_isValidDataItem(dataItem) {
			if (!dataItem.selection) {
				return false;
			}
			const sortingSpec = dataItem.selection.filter(value => Object.keys(value).includes('sort'));
			return sortingSpec.length > 0;
		}
		/**
		 *
		 * Turn off the multiEdgeSort(There is only one edge which is the shared slot)
		 * @param {*} querySpec query spec object
		 * @memberof SharedSlotVisQueryModifier
		 */
		_modifyMultiEdgeSort(querySpec) {
			if(querySpec.spec.queryHints) {
				querySpec.spec.queryHints.multiEdgeSort = false;
			}
		}
	}
	return SharedSlotVisQueryModifier;
});